import {
  getNewKey,
  isEditableInstallment,
  sortInstallmentsByDate,
} from 'components/common/balance-adjustment/installmentHelpers';
import {
  FormAnswer,
  FormQuestion,
  QuestionType,
} from 'components/registration/v2/PostRegEditDrawer';
import { InstallmentEditable } from 'components/registration/v2/types';
import { Dispatch } from 'react';
import {
  formatDateValue,
  formatPendingEdits,
  formatValue,
  getInstallmentErrors,
  getLocalUpdatedFlags,
  getQuestion,
  getQuestionDefaults,
} from './helpers';
import { PostRegDrawerAction } from './reducer';
import { getPreviewLineItems, saveAllEdits } from './service';
import {
  ActionType,
  Confirmation,
  FormPage,
  LoadingType,
  PendingEdit,
  PendingPayment,
  PostRegDrawerState,
  PostRegEditFormState,
  PreviewLineItems,
} from './types';

export interface PostRegDrawerActions {
  // Returns the original form state before the user edited.
  getOriginalFormState: () => PostRegEditFormState;
  // Updates the form state with the given values.
  updateFormValue: (questionId: string, value: string[]) => void;
  updateFormDetails: (
    questionId: string,
    value: string,
    details: string
  ) => void;
  updateFormFieldFile: (questionId: string, value: string, file: File) => void;
  closeDrawer: () => void;
  setFormPage: (formPage: FormPage) => void;
  getOriginalAndCurrentValues: (questionId: string) => {
    originalValue: string;
    currentValue: string;
    question: FormQuestion;
  };
  getPreviewLineItems: () => Promise<void>;
  setPendingEdits: () => void;
  updatePendingPayment: (pendingPayment: PendingPayment) => void;
  validatePendingPayment: () => void;
  validateAndSubmit: () => Promise<void>;
  setLoading: (loadingType: LoadingType, isLoading: boolean) => void;
}

export const getActions = (
  state: PostRegDrawerState,
  dispatch: Dispatch<PostRegDrawerAction>
): PostRegDrawerActions => ({
  setLoading: (loadingType, isLoading) => {
    dispatch({
      type: ActionType.SetLoading,
      loading: {
        loadingType,
        isLoading,
      },
    });
  },
  getOriginalFormState: () => {
    const { currentFormState, formStructure } = state;
    const updatedFormState = { ...currentFormState };
    Object.keys(updatedFormState).forEach((questionId) => {
      const question = getQuestion(formStructure, questionId);
      updatedFormState[questionId] = getQuestionDefaults(question);
    });
    return updatedFormState;
  },
  updateFormDetails: (questionId, value, details) => {
    dispatch({
      type: ActionType.UpdateFormFieldDetails,
      payload: {
        questionId,
        value,
        details,
      },
    });
  },
  updateFormFieldFile: (questionId, value, file) => {
    dispatch({
      type: ActionType.UpdateFormFieldFile,
      payload: {
        questionId,
        value,
        file,
      },
    });
  },
  updateFormValue: (questionId, values) => {
    dispatch({
      type: ActionType.UpdateFormField,
      payload: {
        questionId,
        values,
      },
    });
  },
  closeDrawer: () => {
    dispatch({ type: ActionType.ResetState });
  },
  setFormPage: (formPage) => {
    dispatch({
      type: ActionType.setFormPage,
      payload: { formPage },
    });
  },
  setPendingEdits: () => {
    const pendingEdits = formatPendingEdits(state);
    dispatch({
      type: ActionType.SetPendingEdits,
      payload: { pendingEdits },
    });
  },
  getOriginalAndCurrentValues: (questionId) => {
    const current: FormAnswer[] = state.currentFormState[questionId];
    const question = getQuestion(state.formStructure, questionId);
    const { type } = question;
    const original = getQuestionDefaults(question);

    const originalValue = original
      .map((ans) =>
        type === QuestionType.DATE
          ? formatDateValue(question, ans)
          : formatValue(question, ans)
      )
      .join(', ');
    const currentValue = current
      .map((ans) =>
        type === QuestionType.DATE
          ? formatDateValue(question, ans)
          : formatValue(question, ans)
      )
      .filter(Boolean)
      .join(', ');

    return { originalValue, currentValue, question };
  },
  getPreviewLineItems: async () => {
    dispatch({
      type: ActionType.SetLoading,
      loading: {
        loadingType: LoadingType.GetPreviewLineItems,
        isLoading: true,
      },
    });
    try {
      const { formResultId, orderId, pendingEdits } = state
      const { data: previewLineItems } =
        await getPreviewLineItems<PreviewLineItems>(formResultId, orderId, pendingEdits);
      dispatch({
        type: ActionType.SetPreviewLineItems,
        payload: { previewLineItems },
      });
      const installments = previewLineItems?.installments || [];
      let savedPaymentMethod = null;
      const editableInstallments = installments
        .filter(isEditableInstallment)
        .map((installment) => {
          if (installment.paymentMethod && !savedPaymentMethod) {
            savedPaymentMethod = installment.paymentMethod;
          }
          return {
            _key: getNewKey(),
            amount: installment.amount,
            status: installment.status,
            scheduledDate: installment.scheduledDate,
            originalDate: installment.scheduledDate,
            id: installment.id,
          } as InstallmentEditable;
        });
      editableInstallments.sort(sortInstallmentsByDate);
      const paidBalance = installments.reduce((acc, installment) => {
        if (installment.status === 'paid') {
          return acc + installment.amount;
        }
        return acc;
      }, 0);
      dispatch({
        type: ActionType.SetPendingPayment,
        payload: {
          pendingPayment: {
            paidBalance,
            savedPaymentMethod,
            installments: editableInstallments,
          },
        },
      });
      dispatch({
        type: ActionType.SetLoading,
        loading: {
          loadingType: LoadingType.GetPreviewLineItems,
          isLoading: false,
        },
      });
    } catch (error) {
      dispatch({
        type: ActionType.SetLoading,
        loading: {
          loadingType: LoadingType.GetPreviewLineItems,
          isLoading: false,
          error: error.message || 'An error occurred',
        },
      });
    }
  },
  updatePendingPayment: (pendingPaymentUpdates) => {
    const { pendingPayment: currentPendingPayment } = state;
    dispatch({
      type: ActionType.SetPendingPayment,
      payload: {
        pendingPayment: {
          ...(currentPendingPayment || {}),
          ...pendingPaymentUpdates,
          fieldErrors: {},
          error: null,
          installmentsUpdated: true,
        },
      },
    });
  },
  validatePendingPayment: () => {
    const { pendingPayment: currentPendingPayment } = state;
    const { hasError, error, fieldErrors } = getInstallmentErrors(state);
    if (hasError) {
      dispatch({
        type: ActionType.SetPendingPayment,
        payload: {
          pendingPayment: {
            ...(currentPendingPayment || {}),
            fieldErrors,
            error,
          },
        },
      });
      return;
    }
  },
  validateAndSubmit: async () => {
    const { pendingPayment, pendingEdits, formResultId, orderId } = state;
    const installments = pendingPayment?.installments || [];
    const { hasError, error, fieldErrors } = getInstallmentErrors(state);

    if (hasError) {
      dispatch({
        type: ActionType.SetPendingPayment,
        payload: {
          pendingPayment: {
            ...(pendingPayment || {}),
            fieldErrors,
            error,
            ...installments,
          },
        },
      });
      return Promise.reject({ error, fieldErrors });
    }
    dispatch({
      type: ActionType.SetLoading,
      loading: { loadingType: LoadingType.SubmitEdits, isLoading: true },
    });
    try {
      const { additionalQuestionsUpdated, participantGroupUpdated, feeRelatedQuestionUpdated } =
        getLocalUpdatedFlags(state);
      const skipSendingInstallments = !pendingPayment?.installmentsUpdated && !feeRelatedQuestionUpdated;
      const formattedInstallments = skipSendingInstallments ? undefined : pendingPayment?.installments.map(
        (installment) => ({
          amount: installment.amount,
          canceled: installment.status === 'canceled', // BE only needs to know if it's canceled
          scheduledDate: installment.scheduledDate,
          id: installment.id,
        })
      ); // parse to match the expected format

      const { data } = await saveAllEdits(
        formResultId,
        orderId,
        pendingEdits,
        formattedInstallments
      );
      dispatch({
        type: ActionType.SetLoading,
        loading: { loadingType: LoadingType.SubmitEdits, isLoading: false },
      });
      dispatch({
        type: ActionType.SetConfirmation,
        payload: {
          confirmation: {
            ...(data as Partial<Confirmation>),
            additionalQuestionsUpdated,
            participantGroupUpdated,
          } as Confirmation,
        },
      });
    } catch (error) {
      dispatch({
        type: ActionType.SetLoading,
        loading: {
          loadingType: LoadingType.SubmitEdits,
          isLoading: false,
          error: error.message || 'An error occurred',
        },
      });
      return Promise.reject(error);
    }
  },
});
